import { useLazyQuery, useMutation, useQuery } from "@apollo/client/react";
import { gql } from "graphql-tag";
import {
  buildConcentrationAvailabilityAnswers,
  type QuestionLike,
} from "@portal/app/_common/lib/formFunnelDynamicOptions";
import {
  FormFunnelDocument,
  GetFormFunnelAvailableConcentrationsDocument,
  GetFormFunnelMatchesDocument,
  SubmitFormFunnelLeadDocument,
  type GetFormFunnelAvailableConcentrationsQuery,
  type GetFormFunnelAvailableConcentrationsQueryVariables,
} from "./generated";

/**
 * Availability is read declaratively for live UX filtering (Apollo caches by
 * variables) and imperatively (lazy) for the backstop scrub run against the
 * final answers just before matching — see FormFunnelPage. The whole ENG-7917
 * dedupe rests on both reads staying `cache-first` with byte-identical
 * variables, so the second resolves from cache instead of doubling an
 * unauthenticated, high-volume request. Both halves are owned here, in the one
 * module the call sites and the dedupe test consume: the wrappers pin the
 * fetch policy (`cache-first` is Apollo's default — pinning guards against a
 * future client-level default or a call-site override, which the
 * `fetchPolicy`/`nextFetchPolicy` `?: never` members turn into compile
 * errors), and `buildConcentrationAvailabilityVariables` is the only
 * sanctioned way to assemble the variables. The explicit `variables` member
 * below preserves required variables while Apollo 3's direct query hook
 * otherwise allows the entire variables object to be omitted.
 */
export const useFormFunnelAvailabilityQuery = (
  options: useQuery.Options<
    GetFormFunnelAvailableConcentrationsQuery,
    GetFormFunnelAvailableConcentrationsQueryVariables
  > & {
    variables: GetFormFunnelAvailableConcentrationsQueryVariables;
    fetchPolicy?: never;
    nextFetchPolicy?: never;
  }
) =>
  useQuery(GetFormFunnelAvailableConcentrationsDocument, {
    ...options,
    fetchPolicy: "cache-first",
  });

export const useFormFunnelAvailabilityLazyQuery = () =>
  useLazyQuery(GetFormFunnelAvailableConcentrationsDocument, {
    fetchPolicy: "cache-first",
    notifyOnNetworkStatusChange: true,
  });

/**
 * The variables for both availability reads. The answers projection (dropping
 * the user's own concentration picks) is what keeps the cache key stable
 * across the two reads — see buildConcentrationAvailabilityAnswers.
 */
export const buildConcentrationAvailabilityVariables = (
  funnelId: string,
  questions: readonly QuestionLike[],
  answers: Record<string, unknown>
): GetFormFunnelAvailableConcentrationsQueryVariables => ({
  funnelId,
  answers: buildConcentrationAvailabilityAnswers(questions, answers),
  geo: { geoCenter: {} },
});

void gql`
  query formFunnel($slug: String!) {
    getFormFunnelConfig(slug: $slug) {
      id
      slug
      name
      meta {
        title
        description
      }
      questions {
        id
        slug
        label
        type
        options {
          label
          value
          icon
        }
        required
        conditionalLogic
      }
    }
  }
  query getFormFunnelMatches(
    $funnelId: ID!
    $answers: Json!
    $geo: DreamSearchGeo!
    $jornayaLeadId: String
    $contactData: ContactDataInput
    $sub1: String
  ) {
    getFormFunnelMatches(
      funnelId: $funnelId
      answers: $answers
      geo: $geo
      jornayaLeadId: $jornayaLeadId
      contactData: $contactData
      sub1: $sub1
    ) {
      scenario
      maxResults
      redirectUrl
      targetProgramId
      targetProgramName
      socialRedirectUrl
      prePingResult
      needsContact
      isErrorFallback
      resultMatches {
        rank
        scenario
        prePingResult
        match {
          id
          distance
          score
          program {
            id
            name
            slug
            environment
            preLeadRedirect
            preLeadRedirectParams
            preLeadRedirectCompositeParams
            preLeadRedirectSuppressDefaultParams
            customCtaCopy
            customDisclaimer
            programHighlights
            isSendingPaidLeadsEnabled
            requiredFields
            dobFieldVisibility
            allowedZipCodes
            hsGradYearMin
            hsGradYearMax
            vocation {
              id
              industry
            }
            paymentOptions(filter: { type: SCHOOL_FINANCIAL_AID, isVisible: true }) {
              id
              isActive
            }
          }
          provider {
            id
            fullName
            shortName
            advertiserName
            slug
            isPartnered
            isTrustedFormEnabled
            successPageMessage
            logoImageFile {
              url
            }
          }
        }
      }
      topMatch {
        id
        distance
        score
        program {
          id
          name
          slug
          environment
          preLeadRedirect
          preLeadRedirectParams
          preLeadRedirectCompositeParams
          preLeadRedirectSuppressDefaultParams
          customCtaCopy
          customDisclaimer
          programHighlights
          isSendingPaidLeadsEnabled
          requiredFields
          dobFieldVisibility
          allowedZipCodes
          hsGradYearMin
          hsGradYearMax
          vocation {
            id
            industry
          }
          paymentOptions(filter: { type: SCHOOL_FINANCIAL_AID, isVisible: true }) {
            id
            isActive
          }
        }
        provider {
          id
          fullName
          shortName
          advertiserName
          slug
          isPartnered
          isTrustedFormEnabled
          successPageMessage
          logoImageFile {
            url
          }
        }
      }
      matches {
        id
        distance
        favorited
        score
        program {
          id
          name
          slug
        }
        provider {
          id
          fullName
          slug
          logoImageFile {
            url
          }
        }
      }
    }
  }
  query getFormFunnelAvailableConcentrations(
    $funnelId: ID!
    $answers: Json!
    $geo: DreamSearchGeo!
  ) {
    getFormFunnelAvailableConcentrations(funnelId: $funnelId, answers: $answers, geo: $geo) {
      subindustries
      vocationIds
    }
  }
  mutation submitFormFunnelLead(
    $funnelId: ID!
    $programId: ID!
    $answers: Json!
    $formFunnelSessionId: ID
    $customerData: CustomerFormDataInput
    $complianceTracking: ComplianceTrackingDataInput!
    $utmTracking: TrackingDataInput
    $disclaimer: String
    $geo: DreamSearchGeo
    $prePingResult: Json
    $selectedResultRank: Int
  ) {
    submitFormFunnelLead(
      funnelId: $funnelId
      programId: $programId
      answers: $answers
      formFunnelSessionId: $formFunnelSessionId
      customerData: $customerData
      complianceTracking: $complianceTracking
      utmTracking: $utmTracking
      disclaimer: $disclaimer
      geo: $geo
      prePingResult: $prePingResult
      selectedResultRank: $selectedResultRank
    ) {
      success
      enrollmentItemId
      scenario
      redirectUrl
      customerId
      marketplaceEventId
      everflowTransactionId
      eligibilityFailure
    }
  }
`;

export const useFormFunnelConfig = (slug?: string) => {
  const { data, loading, error, refetch } = useQuery(FormFunnelDocument, {
    variables: { slug: slug! },
    skip: !slug,
    fetchPolicy: "cache-and-network",
  });

  const funnelConfig = data?.getFormFunnelConfig;

  return {
    funnelConfig,
    loading,
    error,
    refetch,
  };
};

export const useFormFunnelMatch = () => {
  const [getMatches, { data, loading, error }] = useLazyQuery(GetFormFunnelMatchesDocument, {
    notifyOnNetworkStatusChange: true,
  });

  const matchResult = data?.getFormFunnelMatches;
  const scenario = matchResult?.scenario;
  const topMatch = matchResult?.topMatch;
  const matches = matchResult?.matches;
  const redirectUrl = matchResult?.redirectUrl;
  const prePingResult = matchResult?.prePingResult ?? null;

  return {
    getMatches,
    matchResult,
    scenario,
    topMatch,
    matches,
    redirectUrl,
    prePingResult,
    loading,
    error,
  };
};

export const useSubmitLead = () => {
  const [submitLead, { data, loading, error }] = useMutation(SubmitFormFunnelLeadDocument);

  const result = data?.submitFormFunnelLead;

  return {
    submitLead,
    result,
    success: result?.success,
    enrollmentItemId: result?.enrollmentItemId,
    scenario: result?.scenario,
    redirectUrl: result?.redirectUrl,
    loading,
    error,
  };
};
